import {
  proStudioUrqlGql as urqlGql,
  SolutionVersion as GeneratedSolutionVersion,
} from '@pypestream/api-services';
// `/urql` subpath for the Agent Manager file queries — same reason as
// `use-agent-manager-files.ts`: the package root's types lag behind new operations.
import { proStudioUrqlGql as agentManagerGql } from '@pypestream/api-services/urql';
import { throwErrorWithId } from '@pypestream/utils';

import {
  ProStudioEvents,
  ProStudioContext,
  SelectedSolution,
} from '../prostudio.xstate.types';
import {
  GetBotData,
  GetEnvsList,
  GetVersionDataQuery,
  SolutionVersion,
} from '../prostudio.api-service.types';
import {
  buildVersionHistory,
  sortByVersionDescending,
  buildVersionPsIdCandidates,
  checkForErrors,
  deriveDeployedVersions,
  pickDraftVersion,
  pickVersionPsId,
  readBotType,
  readVersionName,
  readVersionNames,
  SOLUTION_ENVIRONMENTS,
  type DeploymentLike,
  isSelectedBotStale,
} from '../../utils';
import { reportDegradedLoad } from './helpers/report-degraded-load';
import { loadAgentManagerAssets } from '../../utils/load-agent-manager-assets';

/**
 * Every deployment of the solution, for the history list — or `null` when it cannot be
 * read, in which case the caller falls back to the per-environment latest it already has.
 */
const loadDeploymentHistory = async (
  solutionId: string | undefined
): Promise<DeploymentLike[] | null> => {
  if (!solutionId) return null;
  try {
    const { data, error } = await agentManagerGql.getSolutionDeploymentHistory({
      solutionId,
    });
    if (error) throw error;
    const rows = data?.admin_?.solutionById?.solutionDeployments;
    return rows ? (rows.filter(Boolean) as DeploymentLike[]) : null;
  } catch (error) {
    reportDegradedLoad(error, 'load-bot-versions:deployment-history', {
      solutionId,
    });
    return null;
  }
};

/**
 * The draft's media, in the shape `setSelectedSolution` attaches (`selectedBot.assets`).
 * Best-effort: a solution whose media cannot be listed still opens, with an empty tab.
 */
const loadDraftAssets = async (
  solutionId: string | undefined,
  versionName: string
): Promise<{ id: string; url: string }[]> => {
  if (!solutionId || !versionName) return [];
  try {
    // Carries each file's repository `path` too, which a Media delete needs.
    return await loadAgentManagerAssets(solutionId, versionName);
  } catch (error) {
    reportDegradedLoad(error, 'load-bot-versions:draft-assets', {
      solutionId,
      versionName,
    });
    return [];
  }
};

/**
 * The draft's uploaded scripts (`services/*.py`), as the Agent Manager has them.
 *
 * `ctx.draft.files` is what the agent compiler's missing-script rule and the command
 * picker read, and Bot Manager used to fill it. For a v2 solution nothing did, so every
 * action node calling an uploaded script failed validation with "Solution does not have
 * all action node scripts uploaded" while the Files tab listed the script (PSTUDIO-3336).
 *
 * Best-effort: a solution whose file list cannot be read still opens. The validator then
 * reports the scripts as missing, which blocks publish rather than passing it.
 */
const loadDraftScriptPaths = async (
  solutionId: string | undefined,
  versionName: string
): Promise<string[]> => {
  if (!solutionId) return [];
  try {
    const { data, error } = await agentManagerGql.getScriptsFiles({
      solutionId,
      versionName,
    });
    if (error) throw error;
    return (data?.admin_?.scriptsFiles ?? [])
      .map((file) => file?.path)
      .filter((path): path is string => Boolean(path));
  } catch (error) {
    reportDegradedLoad(error, 'load-bot-versions:draft-scripts', {
      solutionId,
      versionName,
    });
    return [];
  }
};

export const completeVersions = (
  versions: string[],
  data: SolutionVersion[]
): SolutionVersion[] => {
  const existingVersions = new Set(data.map((item) => item.version));

  const missingEntries: SolutionVersion[] = versions
    .filter((version) => !existingVersions.has(version))
    .map((version) => ({
      bot: '',
      customer: '',
      email: '',
      env: '',
      first_name: '',
      last_name: '',
      time: '',
      uuid: '',
      version,
      picture: '',
    }));

  return [...data, ...missingEntries];
};

// Lives with the history helpers so `mergeDeploymentHistory` can share it without
// pulling this invoke's import graph; re-exported for existing callers.
export { sortByVersionDescending };

export const loadBotVersions = async (
  ctx: ProStudioContext,
  _event: ProStudioEvents
): Promise<{
  versions: SolutionVersion[];
  deployedVersions: GetBotData['getEnvBotVersions']['data']['envs'];
  selectedBot: SelectedSolution;
  draftVersion: string | null;
  solutionVersionPsId: string | null;
  envs: GetEnvsList['getListEnvs']['data'];
  draftData: GetVersionDataQuery['getVersionById']['data'] | null;
  draftVersionSolutionData: GeneratedSolutionVersion | null;
  solutionVersions: GeneratedSolutionVersion[];
  corpusSettings: {
    liveCorpusName: string;
    sandboxCorpusName: string;
  };
} | void> => {
  const {
    botName,
    customerId,
    selectedBot: ctxSelectedBot,
    project,
    solutions,
  } = ctx;

  const projectId = project?.id;

  if (!projectId) {
    throwErrorWithId('Project ID is required');
    return;
  }

  if (!botName) {
    throwErrorWithId('Bot Name is required');
    return;
  }

  if (!customerId) {
    throwErrorWithId('Customer ID is required');
    return;
  }

  const selectedSolution = solutions?.find(
    (solution) => solution?.name === botName
  );

  // PSTUDIO-4121. A browser POP updates `ctx.botName` through the route loader
  // but dispatches no `setSelectedSolution`, so `ctx.selectedBot` can still hold
  // the solution the user came FROM. Below, this decides whether the context
  // copy may stand in for a `GetBotById` at all.
  const ctxSelectedBotIsStale = isSelectedBotStale({
    selectedBot: ctxSelectedBot,
    botName,
  });

  const solutionId = selectedSolution?.id;

  // Bot Manager is retired: `postHistorySearch` is gone from the schema entirely, and
  // `getBotData` / `getBotById` 404 with "Bot does not exist." for any solution created
  // after the migration. Everything here now comes from `solutionById`, which carries
  // both the versions and their deployments.
  const [solutionByIdResponse, deploymentHistory] = await Promise.all([
    solutionId ? urqlGql.getSolutionById({ solutionByIdId: solutionId }) : null,
    loadDeploymentHistory(solutionId),
  ]);

  if (solutionByIdResponse) {
    checkForErrors(solutionByIdResponse);
  }

  const solution = solutionByIdResponse?.data?.admin_?.solutionById;
  const corpusSettings = solution?.settings;

  // The query asks for the latest `finished` deployment per environment, so the
  // newest-wins sort inside `deriveDeployedVersions` has nothing left to do — but the
  // environment is carried by the alias rather than a field, so it is attached here for
  // the helpers to key on.
  const deployments: DeploymentLike[] = (
    [
      ['live', solution?.live],
      ['sandbox', solution?.sandbox],
    ] as const
  )
    .filter(([, deployment]) => Boolean(deployment))
    .map(([environment, deployment]) => ({
      ...deployment,
      environment,
    })) as DeploymentLike[];

  const selectedBot = {
    // PSTUDIO-4121: a stale context copy describes a DIFFERENT solution (a browser
    // POP updates `ctx.botName` without dispatching `setSelectedSolution`), so its
    // fields must never be carried under this solution's name.
    ...(ctxSelectedBotIsStale ? {} : (ctxSelectedBot ?? {})),
    // Named after the route's solution on every path. Bot Manager used to supply
    // this; without it the copy has no `botName`, `isSelectedBotStale` answers it
    // as stale, and every in-solution navigation (Versions → Workflows) cleared the
    // scope the builder then waited on forever.
    botName,
    uniqueId: solutionId || '',
    displayName:
      (ctxSelectedBotIsStale ? undefined : ctxSelectedBot?.displayName) ||
      selectedSolution?.displayName ||
      botName,
  } as SelectedSolution;

  const selectedBotVersions = readVersionNames(
    solution?.solutionVersions,
    solution?.defaultDraftVersionId
  );
  // Read off the solution itself, not `selectedBot`: `reloadCurrentBotData` (every
  // successful publish) clears `selectedBot`, and the `findDraftVersion` fallback below
  // guesses "the newest undeployed version" — under draft-only versioning that is a
  // publish SLOT, and the editor would open it as the draft.
  const draftRow = pickDraftVersion(
    solution?.solutionVersions,
    solution?.defaultDraftVersionId
  );
  // History lists every publish; `deployments` holds only what is deployed NOW (one per
  // environment), which left every older publish with an empty date and author.
  const rawItems = buildVersionHistory(deploymentHistory ?? deployments);

  const extractLastIdFromUrl = (url: string): string => {
    if (!url) return '';
    const parts = url.split('/');
    return parts[parts.length - 1] || '';
  };

  const transformedCorpusSettings = {
    liveCorpusName: corpusSettings?.liveCorpusName
      ? extractLastIdFromUrl(corpusSettings?.liveCorpusName)
      : '',
    sandboxCorpusName: corpusSettings?.sandboxCorpusName
      ? extractLastIdFromUrl(corpusSettings?.sandboxCorpusName)
      : '',
  };

  // `rawItems` already carries the deploying user's picture via `deployedBy`, so the
  // per-entry getUserPictureByEmail round trip the Bot Manager history needed is gone.
  const versionsWithPictures = rawItems;

  // Only the draft row names the draft. The old fallbacks — a context copy's
  // `draftVersion`, then `findDraftVersion`'s "newest undeployed version" — both hand
  // back a publish SLOT under draft-only versioning, and the editor would then write
  // into an immutable version. With no draft the throw below is the right answer.
  const draftVersion = (draftRow && readVersionName(draftRow)) || '';

  const versions = sortByVersionDescending(
    completeVersions(selectedBotVersions, versionsWithPictures)
  ).filter(({ version }) => version !== draftVersion);

  let draftData = null;

  let draftVersionSolutionData = null;

  if (draftVersion) {
    // Matched on the version NAME: `botSolutionVersionId` is null for every v2 version,
    // so the old match silently returned undefined and the draft looked empty.
    draftVersionSolutionData = (solution?.solutionVersions?.find((version) =>
      draftRow?.id
        ? version?.id === draftRow.id
        : version?.name === draftVersion
    ) ?? null) as GeneratedSolutionVersion | null;

    // `draft` is only read for `.files` and `.version`. `.files` is the draft's scripts
    // from the Agent Manager (see loadDraftScriptPaths); nothing fetches the old Bot
    // Manager `getVersionById` payload here any more.
    draftData = {
      version: draftVersion,
      files: await loadDraftScriptPaths(solutionId, draftVersion),
    } as unknown as GetVersionDataQuery['getVersionById']['data'];
  } else {
    throwErrorWithId(
      `Something went wrong: Draft version is required for ${botName} solution.`
    );
  }

  // Read off the solution, not the context copy: every successful publish clears
  // `selectedBot` (`reloadCurrentBotData`), as does a direct load or a POP, and a
  // survey solution rebuilt as `'main'` lost its survey-linking publish path and
  // gained the Channels nav. Assets likewise: `setSelectedSolution` attaches them, but
  // nothing did on these entrances, so Media went empty.
  const selectedBotWithAssets = {
    ...selectedBot,
    botType: solution ? readBotType(solution) : selectedBot.botType || 'main',
    assets:
      selectedBot.assets ?? (await loadDraftAssets(solutionId, draftVersion)),
  };

  // Resolved once here, for the whole solution, rather than rebuilt at each call site:
  // the answer needs a round trip (only the row knows, via `isUsingUpdatedId`, which key
  // form it uses) and every consumer wants the same draft-version key.
  const psIdCandidates = buildVersionPsIdCandidates({
    customerId,
    botName,
    versionName: draftVersion,
    versionId: draftRow?.id,
    solutionVersions: solution?.solutionVersions,
  });

  const psKeys = [psIdCandidates.updated, psIdCandidates.legacy].filter(
    (key): key is string => Boolean(key)
  );

  const psKeysResponse = psKeys.length
    ? await urqlGql.getSolutionVersionPsKeys({ keys: psKeys })
    : null;

  if (psKeysResponse) {
    checkForErrors(psKeysResponse);
  }

  const solutionVersionPsId =
    pickVersionPsId(
      psIdCandidates,
      psKeysResponse?.data?.tenancy_solution_versions_ps
    ) || null;

  return {
    solutionVersionPsId,
    envs: SOLUTION_ENVIRONMENTS,
    versions,
    deployedVersions: deriveDeployedVersions(deployments),
    selectedBot: selectedBotWithAssets,
    draftVersion,
    draftData,
    draftVersionSolutionData,
    corpusSettings: transformedCorpusSettings,
    solutionVersions: (solution?.solutionVersions ||
      []) as GeneratedSolutionVersion[],
  };
};
